import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  alpha,
  Box,
  CircularProgress,
  type Palette,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  type EligibilityCardState,
  type EligibilityReadiness,
  useEligibilityCardState,
} from '../hooks/useEligibilityCardState';


// ── Helpers ────────────────────────────────────────────────────────────

const FIELD_LABELS: Record<string, string> = {
  first_name: 'first name',
  last_name: 'last name',
  dob: 'date of birth',
  zip: 'zip code',
};

function buildMissingFieldsSubtitle(readiness: EligibilityReadiness): string {
  if (readiness.missingBillingNpi) {
    return 'Configure a billing NPI in Settings \u2192 Billing to check eligibility';
  }

  const labels = readiness.missingPatientFields.map((f) => FIELD_LABELS[f] ?? f);
  if (labels.length === 0) return '';
  if (labels.length === 1) return `Add patient ${labels[0]} to check eligibility`;
  const last = labels[labels.length - 1];
  const rest = labels.slice(0, -1);
  return `Add patient ${rest.join(', ')}, and ${last} to check eligibility`;
}

// ── Card config per state ──────────────────────────────────────────────

interface CardConfig {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  borderStyle: 'dashed' | 'solid';
  borderColor: string;
  hoverBorderColor: string;
  hoverBgColor: string;
  bgColor: string;
  iconBg: string;
  iconColor: string;
  titleColor: string;
  arrowColor: string;
  showArrow: boolean;
  showRunButton: boolean;
}

function getStatusPalette(status: string | undefined): 'success' | 'warning' | 'error' {
  if (status === 'eligible') return 'success';
  if (status === 'blocked') return 'error';
  return 'warning';
}

function getCardConfig(
  state: EligibilityCardState,
  palette: Palette,
  readiness: EligibilityReadiness,
  statusSummary?: string,
  statusLight?: string,
): CardConfig | null {
  switch (state) {
    case 'hidden':
      return null;
    case 'needs_info':
      return {
        icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
        title: readiness.missingBillingNpi
          ? 'Billing Setup Required'
          : 'Patient Info Incomplete',
        subtitle: buildMissingFieldsSubtitle(readiness),
        borderStyle: 'dashed',
        borderColor: alpha(palette.warning.main, 0.25),
        hoverBorderColor: alpha(palette.warning.main, 0.4),
        hoverBgColor: alpha(palette.warning.main, 0.05),
        bgColor: alpha(palette.warning.main, 0.02),
        iconBg: alpha(palette.warning.main, 0.1),
        iconColor: 'warning.main',
        titleColor: 'text.primary',
        arrowColor: 'warning.main',
        showArrow: true,
        showRunButton: false,
      };
    case 'needs_check':
      return {
        icon: <VerifiedUserIcon sx={{ fontSize: 18 }} />,
        title: 'Check Patient Eligibility',
        subtitle: 'Run an eligibility check to verify coverage before this visit',
        borderStyle: 'dashed',
        borderColor: alpha(palette.info.main, 0.25),
        hoverBorderColor: alpha(palette.info.main, 0.4),
        hoverBgColor: alpha(palette.info.main, 0.05),
        bgColor: alpha(palette.info.main, 0.02),
        iconBg: alpha(palette.info.main, 0.1),
        iconColor: 'info.main',
        titleColor: 'text.primary',
        arrowColor: 'info.main',
        showArrow: false,
        showRunButton: true,
      };
    case 'check_complete': {
      const pal = getStatusPalette(statusLight);
      const color = palette[pal].main;
      return {
        icon: <VerifiedUserIcon sx={{ fontSize: 18 }} />,
        title: statusSummary ?? 'Eligibility Verified',
        subtitle: 'View full details in the patient billing tab',
        borderStyle: 'solid',
        borderColor: alpha(color, 0.35),
        hoverBorderColor: alpha(color, 0.55),
        hoverBgColor: alpha(color, 0.05),
        bgColor: alpha(color, 0.02),
        iconBg: alpha(color, 0.15),
        iconColor: `${pal}.main`,
        titleColor: 'text.primary',
        arrowColor: 'text.secondary',
        showArrow: true,
        showRunButton: false,
      };
    }
  }
}

// ── Component ──────────────────────────────────────────────────────────

interface EligibilityCheckCardProps {
  patientId?: string | null;
  encounterId?: string | null;
  /** When provided, called instead of navigating to the patient page for needs_info click */
  onEditPatientInfo?: () => void;
}

export function EligibilityCheckCard({ patientId, encounterId, onEditPatientInfo }: EligibilityCheckCardProps) {
  const navigate = useNavigate();
  const { palette } = useTheme();

  const { state, eligibilityData, isLoading, runCheck, isRunning, readiness } =
    useEligibilityCardState({
      patientId,
      encounterId,
    });

  const config = getCardConfig(
    state,
    palette,
    readiness,
    eligibilityData?.summary,
    eligibilityData?.status,
  );

  const handleClick = useCallback(() => {
    if (state === 'needs_info' && readiness.missingBillingNpi) {
      navigate('/practice/billing-settings');
      return;
    }
    if (state === 'needs_info' && patientId) {
      if (onEditPatientInfo) {
        onEditPatientInfo();
      } else {
        navigate(`/patient/${patientId}`, {
          state: { openEditSidebar: true },
        });
      }
      return;
    }
    if (state === 'needs_check') {
      runCheck();
      return;
    }
    if (state === 'check_complete' && patientId) {
      navigate(`/patient/${patientId}`, {
        state: { tab: 'billing', encounterId },
      });
    }
  }, [state, navigate, runCheck, patientId, encounterId, readiness.missingBillingNpi, onEditPatientInfo]);

  if (isLoading) {
    return <Skeleton variant="rounded" height={64} sx={{ width: '100%', borderRadius: 2 }} />;
  }

  if (!config) return null;

  const isInteractive =
    state === 'needs_check' ||
    state === 'check_complete' ||
    state === 'needs_info';

  return (
    <Box>
      <Paper
        component={isInteractive ? 'button' : 'div'}
        variant="outlined"
        onClick={isInteractive ? handleClick : undefined}
        disabled={state === 'needs_check' && isRunning}
        sx={{
          width: '100%',
          border: `2px ${config.borderStyle}`,
          borderColor: config.borderColor,
          borderRadius: 2,
          bgcolor: config.bgColor,
          cursor: !isInteractive
            ? 'default'
            : state === 'needs_check' && isRunning
              ? 'wait'
              : 'pointer',
          textAlign: 'left',
          p: 0,
          transition: 'border-color 0.15s, background-color 0.15s',
          ...(isInteractive && {
            '&:hover': {
              borderColor: config.hoverBorderColor,
              bgcolor: config.hoverBgColor,
            },
            '&:hover .card-arrow': {
              transform: 'translateX(2px)',
            },
          }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, gap: 2 }}>
          {/* Icon */}
          <Box
            sx={{
              bgcolor: config.iconBg,
              color: config.iconColor,
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.15s',
            }}
          >
            {config.icon}
          </Box>

          {/* Copy */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: config.titleColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {config.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}
            >
              {config.subtitle}
            </Typography>
          </Box>

          {/* Trailing action */}
          {config.showArrow && (
            <ArrowForwardIcon
              className="card-arrow"
              sx={{
                fontSize: 18,
                flexShrink: 0,
                color: config.arrowColor,
                transition: 'transform 0.15s',
              }}
            />
          )}
          {config.showRunButton &&
            (isRunning ? (
              <CircularProgress size={20} sx={{ flexShrink: 0 }} />
            ) : (
              <PlayArrowIcon
                sx={{
                  fontSize: 22,
                  flexShrink: 0,
                  color: 'info.main',
                }}
              />
            ))}
        </Box>
      </Paper>

    </Box>
  );
}
